import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { Inventory } from '@libs/common-node';
import type { StoreSearchParams } from '@libs/common-types';

import { searchStores } from '../api';
import { enhanceStoresWithInventory } from '../libs';
import { useQueryInventoryPairs } from './use-query-inventory-pairs';

type StoreSku = { store: string; sku: string };

export const useStoreSearchQuery = ({
    minimumCaseQty = 0,
    sku,
    enabled = true,
    ...params
}: StoreSearchParams & { minimumCaseQty?: number; enabled?: boolean }) => {
    const queryKey = ['storeSearch', sku, params];

    const searchQuery = useQuery({
        queryKey: queryKey,
        queryFn: () => searchStores({ includeAttributes: Boolean(sku), ...params, sku }),
        staleTime: 60 * 60 * 1000, // 1hr
        enabled,
    });
    const stores = useMemo(() => (enabled ? searchQuery.data || [] : []), [enabled, searchQuery.data]);

    const storesku: StoreSku[] = useMemo(
        () =>
            sku
                ? stores.reduce<StoreSku[]>((acc, store) => {
                      if (store?.Store?.StoreNumber) {
                          acc.push({
                              store: `${store.Store.StoreNumber}`,
                              sku: `${sku}`,
                          });
                      }
                      return acc;
                  }, [])
                : [],
        [stores, sku]
    );

    const inventoryQuery = useQueryInventoryPairs({
        storesku,
        options: { enabled: storesku.length > 0 },
    }) as UseQueryResult<Inventory[]>;

    const skuInventory = useMemo(
        () => (inventoryQuery.data || []).find(inventory => inventory.sku === String(sku)) ?? ({} as Inventory),
        [inventoryQuery.data, sku]
    );

    const isLoading = !enabled || searchQuery.isPending || inventoryQuery.isLoading;
    const isError = searchQuery.isError || inventoryQuery.isError;
    const enchancedStoreData = useMemo(
        () => (isLoading ? stores : enhanceStoresWithInventory(skuInventory, stores, minimumCaseQty)),
        [isLoading, skuInventory, stores, minimumCaseQty]
    );

    return {
        isLoading,
        isError,
        data: enchancedStoreData,
    };
};
